import React, { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { OAuthProvider, GoogleAuthProvider, signInWithCredential } from 'firebase/auth';
import { auth } from '../firebase';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  sendEmailVerification,
  sendPasswordResetEmail,
} from 'firebase/auth';

const C = {
  bg:          '#0D1B2A',
  bgCard:      '#13223A',
  bgInput:     '#0A1520',
  border:      'rgba(255,255,255,0.06)',
  orange:      '#FF6B00',
  orangeDim:   'rgba(255,107,0,0.12)',
  textPrimary:   '#FFFFFF',
  textSecondary: '#8FA3C0',
  textMuted:     '#5A7A9A',
  red:       'rgba(255,68,68,0.15)',
  redBorder: 'rgba(255,68,68,0.4)',
  redText:   '#FF8080',
  green:     '#00C851',
  greenDim:  'rgba(0,200,81,0.12)',
  greenBorder:'rgba(0,200,81,0.3)',
};
const font = `'SF Pro Display',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif`;

const googleProvider = new GoogleAuthProvider();

function Login() {
  const [email,        setEmail]        = useState('');
  const [password,     setPassword]     = useState('');
  const [isSignUp,     setIsSignUp]     = useState(false);
  const [error,        setError]        = useState('');
  const [loading,      setLoading]      = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [focusedField, setFocused]      = useState(null);
  const [verifyScreen, setVerifyScreen] = useState(false);
  const [resetSent,    setResetSent]    = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    getRedirectResult(auth).then(result => {
      if (result && result.user) { /* success */ }
    }).catch(err => {
      if (!Capacitor.isNativePlatform() && err.code !== 'auth/null-user') {
        setError(friendlyError(err.code));
      }
    });
  }, []);

  const friendlyError = (code) => {
    switch (code) {
      case 'auth/wrong-password':
      case 'auth/invalid-credential':  return 'Incorrect email or password.';
      case 'auth/user-not-found':      return 'No account found with that email.';
      case 'auth/email-already-in-use':return 'An account already exists with this email.';
      case 'auth/weak-password':       return 'Password must be at least 6 characters.';
      case 'auth/invalid-email':       return 'Please enter a valid email address.';
      case 'auth/too-many-requests':   return 'Too many attempts. Please try again later.';
      case 'auth/popup-closed-by-user':return 'Sign-in cancelled.';
      default:                         return 'Error: ' + code;
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (isSignUp) {
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        await sendEmailVerification(cred.user);
        setVerifyScreen(true);
      } else {
        const cred = await signInWithEmailAndPassword(auth, email, password);
        if (!cred.user.emailVerified) {
          await auth.signOut();
          setError('Please verify your email before signing in. Check your inbox for the verification link.');
          setLoading(false);
          return;
        }
      }
    } catch (err) {
      setError(friendlyError(err.code));
    }
    setLoading(false);
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setError('');
    try {
      const platform = Capacitor.getPlatform();
      if (Capacitor.isNativePlatform() && platform === 'android') {
        const { GoogleAuth } = await import('@codetrix-studio/capacitor-google-auth');
        await GoogleAuth.initialize({
          clientId: '898822929266-pmjgfpp6bd5oqae8akrucfji7p2tp868.apps.googleusercontent.com',
          scopes: ['profile', 'email'],
          forceCodeForRefreshToken: true,
        });
        const googleUser = await GoogleAuth.signIn();
        const credential = GoogleAuthProvider.credential(googleUser.authentication.idToken);
        await signInWithCredential(auth, credential);
      } else if (Capacitor.isNativePlatform() && platform === 'ios') {
        await signInWithPopup(auth, googleProvider);
      } else {
        await signInWithPopup(auth, googleProvider);
      }
    } catch (err) {
      console.error('Google sign in error:', err);
      setError(friendlyError(err.code));
    }
    setGoogleLoading(false);
  };

  const handleForgotPassword = async () => {
    if (!email) { setError('Enter your email address first, then click Forgot Password.'); return; }
    setResetLoading(true);
    setError('');
    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
    } catch (err) {
      setError(friendlyError(err.code));
    }
    setResetLoading(false);
  };

  const handleAppleSignIn = async () => {
    setError('');
    try {
      const { SignInWithApple } = await import('@capacitor-community/apple-sign-in');
      const rawNonce = Array.from(crypto.getRandomValues(new Uint8Array(32)))
        .map(b => b.toString(16).padStart(2, '0')).join('');
      const msgBuffer = new TextEncoder().encode(rawNonce);
      const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
      const hashedNonce = Array.from(new Uint8Array(hashBuffer))
        .map(b => b.toString(16).padStart(2, '0')).join('');
      const result = await SignInWithApple.authorize({
        clientId: 'com.bracketz.app',
        redirectURI: 'https://bracketz-d95e2.firebaseapp.com/__/auth/handler',
        scopes: 'email name',
        nonce: hashedNonce,
      });
      const provider = new OAuthProvider('apple.com');
      const credential = provider.credential({
        idToken: result.response.identityToken,
        rawNonce,
      });
      await signInWithCredential(auth, credential);
    } catch (err) {
      console.error('Apple sign in error:', err);
      if (err.message !== 'USER_CANCELLED' && err.code !== 'USER_CANCELLED')
        setError('Apple sign-in failed. Please try again.');
    }
  };

  const inputStyle = (field) => ({
    width: '100%',
    backgroundColor: C.bgInput,
    border: `1px solid ${focusedField === field ? C.orange : C.border}`,
    borderRadius: '10px',
    padding: '14px 16px',
    color: C.textPrimary,
    fontSize: '15px',
    fontFamily: font,
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
    marginBottom: '12px',
  });

  if (verifyScreen) return (
    <div style={{ minHeight:'100vh', backgroundColor:C.bg, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'24px', fontFamily:font }}>
      <div style={{ width:'100%', maxWidth:'400px', backgroundColor:C.bgCard, borderRadius:'18px', border:`1px solid ${C.border}`, padding:'32px 24px', textAlign:'center' }}>
        <div style={{ fontSize:'48px', marginBottom:'16px' }}>📬</div>
        <div style={{ fontSize:'20px', fontWeight:'800', color:C.textPrimary, marginBottom:'10px' }}>Check your inbox</div>
        <div style={{ fontSize:'13px', color:C.textMuted, lineHeight:'1.6', marginBottom:'24px' }}>
          We sent a verification link to <strong style={{ color:C.textSecondary }}>{email}</strong>. Click the link in the email to activate your account, then come back and sign in.
        </div>
        <button
          onClick={() => { setVerifyScreen(false); setIsSignUp(false); setPassword(''); }}
          style={{ width:'100%', backgroundColor:C.orange, color:'#FFF', border:'none', borderRadius:'10px', padding:'14px', fontSize:'14px', fontWeight:'700', fontFamily:font, cursor:'pointer' }}
        >
          Go to Sign In
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight:'100vh', backgroundColor:C.bg, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'24px', fontFamily:font }}>

      <div style={{ textAlign:'center', marginBottom:'44px' }}>
        <div style={{ width:'72px', height:'72px', background:C.orangeDim, border:`1px solid ${C.orange}`, borderRadius:'20px', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px', fontSize:'32px' }}>🏆</div>
        <div style={{ fontSize:'34px', fontWeight:'800', color:C.orange, letterSpacing:'8px', lineHeight:1 }}>BRACKETZ</div>
        <div style={{ fontSize:'11px', color:C.textMuted, letterSpacing:'3px', marginTop:'8px', textTransform:'uppercase' }}>Daily Bracket Challenge</div>
      </div>

      <div style={{ width:'100%', maxWidth:'400px', backgroundColor:C.bgCard, borderRadius:'18px', border:`1px solid ${C.border}`, padding:'28px 24px' }}>
        <div style={{ fontSize:'18px', fontWeight:'700', color:C.textPrimary, marginBottom:'22px', textAlign:'center' }}>
          {isSignUp ? 'Create Account' : 'Welcome Back'}
        </div>

        <button
          onClick={handleGoogleSignIn}
          disabled={googleLoading}
          style={{ width:'100%', backgroundColor:'#FFFFFF', color:'#1a1a1a', border:'1px solid rgba(255,255,255,0.15)', borderRadius:'10px', padding:'13px 16px', fontSize:'14px', fontWeight:'600', fontFamily:font, cursor:googleLoading?'not-allowed':'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'10px', marginBottom:'16px', opacity:googleLoading?0.7:1 }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          {googleLoading ? 'Signing in...' : 'Continue with Google'}
        </button>

        {Capacitor.isNativePlatform() && (
          <button
            onClick={handleAppleSignIn}
            style={{ width:'100%', backgroundColor:'#000000', color:'#FFFFFF', border:'1px solid rgba(255,255,255,0.15)', borderRadius:'10px', padding:'13px 16px', fontSize:'14px', fontWeight:'600', fontFamily:font, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'10px', marginBottom:'16px' }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
            Sign in with Apple
          </button>
        )}

        <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'16px' }}>
          <div style={{ flex:1, height:'1px', backgroundColor:C.border }} />
          <span style={{ fontSize:'11px', color:C.textMuted, letterSpacing:'1px' }}>OR</span>
          <div style={{ flex:1, height:'1px', backgroundColor:C.border }} />
        </div>

        {error && (
          <div style={{ backgroundColor:C.red, border:`1px solid ${C.redBorder}`, borderRadius:'10px', padding:'12px 14px', marginBottom:'16px', fontSize:'13px', color:C.redText, lineHeight:'1.4' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleEmailSubmit}>
          <input style={inputStyle('email')} type="email" placeholder="Email address" value={email}
            onChange={e => setEmail(e.target.value)} onFocus={() => setFocused('email')} onBlur={() => setFocused(null)} required />
          <input style={inputStyle('password')} type="password" placeholder="Password" value={password}
            onChange={e => setPassword(e.target.value)} onFocus={() => setFocused('password')} onBlur={() => setFocused(null)} required />
          {!isSignUp && resetSent && <div style={{ backgroundColor:'rgba(0,200,81,0.12)', border:'1px solid rgba(0,200,81,0.3)', borderRadius:'10px', padding:'12px 14px', marginBottom:'12px', fontSize:'13px', color:'#00C851', lineHeight:'1.4' }}>Password reset email sent! Check your inbox.</div>}
          {!isSignUp && <div style={{ textAlign:'right', marginBottom:'12px', marginTop:'-6px' }}><button type="button" onClick={handleForgotPassword} disabled={resetLoading} style={{ background:'none', border:'none', color:'#FF6B00', fontSize:'13px', fontFamily:font, cursor:'pointer', padding:0 }}>{resetLoading ? 'Sending...' : 'Forgot password?'}</button></div>}
          <button type="submit" disabled={loading} style={{ width:'100%', backgroundColor:loading?'rgba(255,107,0,0.5)':C.orange, color:'#FFF', border:'none', borderRadius:'10px', padding:'15px', fontSize:'14px', fontWeight:'700', fontFamily:font, letterSpacing:'2px', textTransform:'uppercase', cursor:loading?'not-allowed':'pointer', marginTop:'4px' }}>
            {loading ? 'Please wait...' : isSignUp ? 'Create Account' : 'Sign In'}
          </button>
        </form>

        <div style={{ textAlign:'center', marginTop:'20px', paddingTop:'20px', borderTop:`1px solid ${C.border}` }}>
          <span style={{ fontSize:'13px', color:C.textMuted }}>
            {isSignUp ? 'Already have an account? ' : "Don't have an account? "}
          </span>
          <button onClick={() => { setIsSignUp(!isSignUp); setError(''); }} style={{ background:'none', border:'none', color:C.orange, fontSize:'13px', fontWeight:'700', fontFamily:font, cursor:'pointer', padding:0 }}>
            {isSignUp ? 'Sign In' : 'Sign Up'}
          </button>
        </div>
      </div>

      <div style={{ fontSize:'10px', color:C.textMuted, marginTop:'20px', textAlign:'center', maxWidth:'300px', lineHeight:'1.6' }}>
        Protected by reCAPTCHA Enterprise. By continuing you agree to our Terms of Service and Privacy Policy.
      </div>
    </div>
  );
}

export default Login;
